import { useState, useEffect } from "react";
import { Search, User, Loader2, RefreshCw, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import API from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

const STATUS_COLORS = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  suspended: "bg-amber-50 text-amber-700 border-amber-200",
  terminated: "bg-red-50 text-red-700 border-red-200",
};

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [creditModal, setCreditModal] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditDesc, setCreditDesc] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { loadCustomers(); }, [page, statusFilter]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.append("status", statusFilter);
      if (search) params.append("search", search);
      const { data } = await API.get(`/admin/customers?${params}`);
      setCustomers(data.customers);
      setTotal(data.total);
    } catch { toast.error("Failed to load customers"); }
    finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    setActionLoading(true);
    try {
      await API.put(`/admin/customers/${id}/status`, { status });
      toast.success(`Customer ${status}`);
      loadCustomers();
    } catch { toast.error("Action failed"); }
    finally { setActionLoading(false); }
  };

  const adjustCredits = async () => {
    if (!creditAmount || !selected) return;
    setActionLoading(true);
    try {
      await API.post(`/admin/customers/${selected._id}/credits`, { amount: parseInt(creditAmount), description: creditDesc || "Admin adjustment" });
      toast.success("Credits adjusted");
      setCreditModal(false);
      setCreditAmount("");
      setCreditDesc("");
      loadCustomers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setActionLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">{total} total customers</p>
        </div>
        <Button onClick={loadCustomers} variant="outline" size="sm" className="border-slate-300 text-slate-600 h-9 hover:bg-slate-50">
          <RefreshCw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name, email, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadCustomers()}
            className="pl-9 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-9"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {["", "active", "suspended", "terminated"].map((s) => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors ${statusFilter === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid={ADMIN.customersTable}>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wider">
                <th className="text-left px-5 py-3.5 font-semibold">Customer</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden sm:table-cell">Business</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden md:table-cell">Credits</th>
                <th className="text-left px-5 py-3.5 font-semibold hidden lg:table-cell">SMS Sent</th>
                <th className="text-left px-5 py-3.5 font-semibold">Status</th>
                <th className="text-left px-5 py-3.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}><td colSpan={6} className="px-5 py-4"><div className="h-5 bg-slate-100 rounded animate-pulse" /></td></tr>
                ))
              ) : customers.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400 text-sm">No customers found</td></tr>
              ) : customers.map((c) => (
                <tr key={c._id} data-testid={ADMIN.customerRow} className="table-row-hover">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#dce4f0] flex items-center justify-center text-[#0c1e41] text-sm font-bold shrink-0">
                        {c.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-500">{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell text-sm text-slate-700">{c.business_name || "—"}</td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className="text-sm font-mono font-semibold text-slate-900">{(c.balance || 0).toLocaleString()}</span>
                  </td>
                  <td className="px-5 py-4 hidden lg:table-cell text-sm text-slate-600">{c.sms_sent || 0}</td>
                  <td className="px-5 py-4">
                    <Badge variant="outline" className={`text-xs ${STATUS_COLORS[c.status] || ""}`}>{c.status}</Badge>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={ADMIN.creditButton}
                        onClick={() => { setSelected(c); setCreditModal(true); }}
                        className="text-xs text-[#0c1e41] font-semibold px-2 py-1 bg-[#eef1f8] hover:bg-[#dce4f0] border border-[#c0cfe8] rounded-md transition-colors"
                      >Credits</button>
                      {c.status === "active" ? (
                        <button data-testid={ADMIN.suspendButton} onClick={() => updateStatus(c._id, "suspended")} className="text-xs text-amber-700 font-semibold px-2 py-1 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-md transition-colors">
                          Suspend
                        </button>
                      ) : c.status === "suspended" ? (
                        <button data-testid={ADMIN.activateButton} onClick={() => updateStatus(c._id, "active")} className="text-xs text-emerald-700 font-semibold px-2 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-md transition-colors">
                          Activate
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Credit Modal */}
      <Dialog open={creditModal} onOpenChange={setCreditModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Adjust Credits — {selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-lg p-3 text-sm text-[#060e1c]">
              Current balance: <span className="font-mono font-bold">{selected?.balance?.toLocaleString() || 0}</span> credits
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Amount (use negative to deduct)</Label>
              <Input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} placeholder="e.g. 500 or -100" className="bg-white border-slate-300 text-slate-900 h-10" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Description</Label>
              <Input value={creditDesc} onChange={(e) => setCreditDesc(e.target.value)} placeholder="Reason for adjustment" className="bg-white border-slate-300 text-slate-900 h-10" />
            </div>
            <Button onClick={adjustCredits} disabled={actionLoading || !creditAmount} className="w-full bg-[#0c1e41] hover:bg-[#091629]">
              {actionLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Apply Adjustment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
